import React, { Component } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import PropTypes from 'prop-types';

import {
  MdClose,
  MdKitchen,
  MdArrowBack,
  MdArrowForward
} from 'react-icons/md';

import StoreContext from '../../context/StoreContext';
import KitchenContext from '../../context/KitchenContext';
import { IdengagerContext, idengager ,run} from '../../context/IdengagerContext';
import KitchenList from './KitchenList';
import KitchenIndicator from './KitchenIndicator';
import EmptyKitchen from './EmptyKitchen';
import CookingInfo from './CookingInfo';
import { Button, PrimaryButton } from '../shared/Buttons';

import {
  breakpoints,
  colors,
  fonts,
  spacing,
  dimensions
} from '../../utils/styles';

const KitchenRoot = styled(`div`)`
  background: ${colors.lightest};
  bottom: 0;
  position: fixed;
  right: 0;
  top: -1px;
  transform: translateX(100%);
  transition: transform 0.75s;
  width: 50%;
  will-change: transform;
  z-index: 1000;

  &.open {
    transform: translateX(0%);
  }

  &.closed {
    transform: translateX(100%);
  }

  ::after {
    background-color: ${colors.lightest};
    bottom: 0;
    content: '';
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    transition: all 250ms;
  }

  &.loading {
    ::after {
      opacity: 0.9;
      pointer-events: all;
    }
  }

  @media (min-width: ${breakpoints.desktop2}px) {
    width: ${dimensions.kitchenWidthDesktop};

    &.covered {
      display: none;
    }
  }
`;

const Heading = styled(`header`)`
  align-items: center;
  display: flex;
  height: ${dimensions.headerHeight2};
  justify-content: flex-start;
`;

const Title = styled(`h2`)`
  flex-grow: 1;
  font-family: ${fonts.heading};
  font-size: 1.8rem;
  left: -${dimensions.headerHeight2};
  margin: 0;
  margin-left: ${spacing.md}px;
  position: relative;

  .open & {
    margin-left: calc(${dimensions.headerHeight2} + ${spacing.md}px);

    @media (min-width: ${breakpoints.desktop2}px) {
      margin-left: ${spacing.md}px;
    }
  }
`;

const Content = styled(`div`)`
  bottom: 0;
  overflow-y: auto;
  padding: ${spacing.lg}px;
  position: absolute;
  top: ${dimensions.headerHeight2};
  width: 100%;

  @media (min-width: ${breakpoints.desktop2}px) {
    ::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }
    ::-webkit-scrollbar-thumb {
      background: ${colors.brandBright};
    }
    ::-webkit-scrollbar-thumb:hover {
      background: ${colors.lilac};
    }
    ::-webkit-scrollbar-track {
      background: ${colors.brandLight};
    }
  }
`;

const ItemsNumber = styled(`span`)`
  align-items: center;
  background: ${colors.lemon};
  border-radius: 50%;
  color: ${colors.brandDark};
  display: flex;
  font-size: 1.3rem;
  font-weight: bold;
  height: 36px;
  justify-content: center;
  width: 36px;
`;

const ItemsInKitchen = styled(`div`)`
  align-items: center;
  display: flex;
  font-size: 0.8rem;
  line-height: 1.2;
  text-align: right;

  ${ItemsNumber} {
    margin-left: ${spacing.xs}px;
    margin-right: ${spacing.md}px;
  }
`;

const Costs = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${spacing.sm}px;
`;

const Cost = styled(`div`)`
  display: flex;
  padding: 0 ${spacing.xs}px ${spacing['2xs']}px;

  :last-child {
    padding-bottom: 0;
  }

  span {
    color: ${colors.textMild};
    flex-basis: 60%;
    font-size: 0.9rem;
    text-align: right;
  }

  strong {
    color: ${colors.lilac};
    flex-basis: 40%;
    text-align: right;
  }
`;

const Total = styled(Cost)`
  border-top: 1px solid ${colors.brandBright};
  color: ${colors.brandDark};
  margin-top: ${spacing.xs}px;
  padding-top: ${spacing.sm}px;

  span {
    font-weight: bold;
    text-transform: uppercase;
  }

  strong,
  span {
    color: inherit;
  }
`;

const iconEntry = keyframes`
  0%, 50% {
    transform: scale(0)
  }
  90% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const numberEntry = keyframes`
  0%{
    transform: scale(0)
  }
  90% {
    transform: scale(0.7);
  }
  100% {
    transform: scale(0.6);
  }
`;

const KitchenToggle = styled(Button)`
  background: ${colors.lightest};
  border: none;
  border-radius: 0;
  display: flex;
  height: ${dimensions.headerHeight2};
  justify-content: center;
  left: 0;
  padding: 0;
  position: relative;
  top: 0;
  transform: translateX(-100%);
  transition: all 0.5s ease;
  width: ${dimensions.headerHeight2};

  :focus {
    box-shadow: 0 0 0 1px ${colors.accent} inset;
  }

  .open & {
    background: ${colors.lilac};
    color: ${colors.lightest};
    transform: translateX(0);
  }

  @media (min-width: ${breakpoints.desktop2}px) {
    .open & {
      transform: translateX(-100%);
    }
  }

  svg {
    animation: ${iconEntry} 0.75s ease forwards;
    height: 28px;
    margin: 0;
    width: 28px;
  }

  ${ItemsNumber} {
    animation: ${numberEntry} 0.5s ease forwards;
    position: absolute;
    right: ${spacing['3xs']}px;
    top: ${spacing['3xs']}px;
    transform: scale(0.6);
  }
`;

const CheckOut = styled(PrimaryButton)`
  font-size: 1.25rem;
  margin: ${spacing.lg}px 0 ${spacing.md}px;
  width: 100%;
`;

const Run = styled(PrimaryButton)`
  font-size: 1.25rem;
  margin: ${spacing.lg}px 0 ${spacing.md}px;
  width: 100%;
`;

const BackLink = styled(Button)`
  font-size: 1.25rem;
  margin-bottom: ${spacing.sm}px;
  width: 100%;
`;

class Kitchen extends Component {
  state = {
    className: 'closed',
    isLoading: false
  };

  componentDidUpdate(prevProps) {
    const componentStatusChanged = prevProps.status !== this.props.status;
    const imageBrowserStatusChanged =
      this.props.productImagesBrowserStatus !==
      prevProps.productImagesBrowserStatus;

    if (componentStatusChanged) {
      this.setState({
        className: this.props.status
      });
    }

    if (this.props.isDesktopViewport) {
      if (imageBrowserStatusChanged) {
        if (this.props.productImagesBrowserStatus === 'open') {
          setTimeout(() => {
            this.setState(state => ({
              className: state.className + ' covered'
            }));
          }, 500);
        } else {
          this.setState(state => ({
            className: state.className.replace('covered', '')
          }));
        }
      }
    }
  }

  render() {
   
    const { status, toggle } = this.props;
    const { className } = this.state;

    return (
      <KitchenContext.Consumer>
        {({ kitchenClient, checkout, removeLineItem, getRunResult, updateLineItem, adding }) => {
          const setKitchenLoading = bool => this.setState({ isLoading: bool });

          const handleRemove = itemID => async event => {
            event.preventDefault();
            await removeLineItem(kitchenClient, checkout.id, itemID);
            setKitchenLoading(false);
          };

          const handleRunResult = itemID => async event => {
            event.preventDefault();
            await getRunResult(kitchenClient, checkout.id, itemID);
            setKitchenLoading(false);
          };

          const handleQuantityChange = lineItemID => async quantity => {
            if (!quantity) {
              return;
            }
            await updateLineItem(kitchenClient, checkout.id, lineItemID, quantity);
            setKitchenLoading(false);
          };

          const itemsInKitchen = checkout.lineItems.reduce(
            (total, item) => total + item.quantity,
            0
          );

          return (
            <KitchenRoot
              className={`${className} ${
                this.state.isLoading ? 'loading' : ''
              }`}
            >
              <Heading>
                <KitchenToggle
                  aria-label={`Shopping Kitchen with ${itemsInKitchen} items`}
                  onClick={toggle}
                >
                  {status === 'open' ? (
                    <MdClose />
                  ) : (
                    <>
                      <MdKitchen />
                      {itemsInKitchen > 0 && (
                        <ItemsNumber>{itemsInKitchen}</ItemsNumber>
                      )}
                    </>
                  )}
                </KitchenToggle>
                <KitchenIndicator itemsInKitchen={itemsInKitchen} adding={adding} />
                <Title>Your Run Kitchen</Title>
                <ItemsInKitchen>
                  requests
                  <br />
                  in run Kitchen
                  <ItemsNumber>{itemsInKitchen}</ItemsNumber>
                </ItemsInKitchen>
              </Heading>
              {checkout.lineItems.length > 0 ? (
                <Content>
                  <KitchenList
                    items={checkout.lineItems}
                    handleRemove={handleRemove}
                    handleRunResult={handleRunResult}
                    updateQuantity={handleQuantityChange}
                    setKitchenLoading={setKitchenLoading}
                    isKitchenLoading={this.state.isLoading}
                  />

                  <Costs>
                    <Cost>
                      <span>Units Under Processing:</span>{' '}
                      <strong>DTSUs: {checkout.subtotalPrice}</strong>
                    </Cost>
                  {/*   <Cost>
                      <span>DTSUs:</span> <strong>{checkout.totalTax}</strong>
                    </Cost> */}
                    <Cost>
                      <span>Units Completed:</span> 
                      <strong>DTSUs: {checkout.subtotalPrice}</strong>
                    </Cost>
                    <Total>
                      <span>Total Units Consumed:</span>
                      <strong>DTSUs {checkout.totalPrice}</strong>
                    </Total>
                  </Costs>

                  {/* <CheckOut href={checkout.webUrl}> */}
                  <CheckOut  onClick={e => run("test")}>  
                    Refresh Status <MdArrowForward />
                  </CheckOut>
                  <BackLink onClick={toggle}>
                    <MdArrowBack />
                    Back to Your Dashboard
                  </BackLink>


                  <CookingInfo />
                </Content>
              ) : (
                <EmptyKitchen />
              )}
            </KitchenRoot>
          );
        }}
    </KitchenContext.Consumer>
    );
  }
}

Kitchen.propTypes = {
  status: PropTypes.string.isRequired,
  toggle: PropTypes.func.isRequired,
  customerAreaStatus: PropTypes.string.isRequired,
  isDesktopViewport: PropTypes.bool,
  productImagesBrowserStatus: PropTypes.string
};

export default Kitchen;
